import { FormEvent, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { ArrowLeft, Mail, Lock, Eye, EyeOff, Loader2, User as UserIcon } from 'lucide-react'
import { apiService } from '@/services/apiService'
import { useAuthStore } from '@/stores/authStore'

/**
 * Password-first signup. An allowlisted member enters their email and chooses
 * a password; the backend creates a confirmed account (no email link / one-time
 * code) and we sign them in immediately. Eliminates the "where do I type the
 * code?" confusion and the junk-folder problem for first-time access.
 */
type Phase = 'idle' | 'not-eligible' | 'exists'

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)

const SignupPage = () => {
  const { signInWithPassword, status, supabaseUser } = useAuthStore()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [phase, setPhase] = useState<Phase>('idle')

  if (status === 'authenticated') {
    const passwordSet = supabaseUser?.user_metadata?.['passwordSet'] === true
    return <Navigate to={passwordSet ? '/dashboard' : '/set-password'} replace />
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    const trimmedFirstName = firstName.trim()
    const trimmedLastName = lastName.trim()
    const trimmed = email.trim().toLowerCase()
    if (!trimmedFirstName || !trimmedLastName) {
      setError('First Name and Last Name are required.')
      return
    }
    if (!isValidEmail(trimmed)) {
      setError('Enter a valid email address.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setSubmitting(true)
    try {
      await apiService.registerMember({
        email: trimmed,
        password,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
      })
      // Account created with the chosen password — sign in right away. The
      // `status === 'authenticated'` redirect above then takes them in.
      await signInWithPassword(trimmed, password)
    } catch (err: any) {
      const code = err?.response?.status
      const msg = err?.response?.data?.error?.message || err?.message || ''
      if (code === 403) {
        setEmail(trimmed)
        setPhase('not-eligible')
      } else if (code === 409) {
        setEmail(trimmed)
        setPhase('exists')
      } else {
        setError(msg || 'Could not create your account. Please try again.')
      }
      setSubmitting(false)
    }
  }

  if (phase === 'not-eligible') {
    return (
      <AuthShell title="Request access" subtitle="Members only — confirmed against the association list">
        <NotEligiblePanel email={email} onTryAnother={() => { setPhase('idle'); setError('') }} />
      </AuthShell>
    )
  }

  if (phase === 'exists') {
    return (
      <AuthShell title="You already have an account" subtitle="This email is already registered">
        <ExistsPanel email={email} onBack={() => { setPhase('idle'); setError('') }} />
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Create your account" subtitle="Members only — set a password to get in">
      <form onSubmit={handleSubmit} className="space-y-5">
        <p className="text-sm text-blue-50/90 leading-relaxed">
          Enter the email the association has on file and choose a password.
          You'll use these to sign in from now on.
        </p>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="block text-sm font-medium text-white mb-2">First Name</span>
            <div className="relative">
              <UserIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                autoComplete="given-name"
                required
                autoFocus
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); if (error) setError('') }}
                className="w-full pl-10 pr-4 py-3 bg-white/95 border border-gray-200 rounded-lg input-focus text-gray-900 placeholder-gray-400"
              />
            </div>
          </label>

          <label className="block">
            <span className="block text-sm font-medium text-white mb-2">Last Name</span>
            <div className="relative">
              <UserIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                autoComplete="family-name"
                required
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); if (error) setError('') }}
                className="w-full pl-10 pr-4 py-3 bg-white/95 border border-gray-200 rounded-lg input-focus text-gray-900 placeholder-gray-400"
              />
            </div>
          </label>
        </div>

        <label className="block">
          <span className="block text-sm font-medium text-white mb-2">Email address</span>
          <div className="relative">
            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError('') }}
              placeholder="you@example.com"
              className="w-full pl-10 pr-4 py-3 bg-white/95 border border-gray-200 rounded-lg input-focus text-gray-900 placeholder-gray-400"
            />
          </div>
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-white mb-2">Create a password</span>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (error) setError('') }}
              placeholder="At least 8 characters"
              className="w-full pl-10 pr-11 py-3 bg-white/95 border border-gray-200 rounded-lg input-focus text-gray-900 placeholder-gray-400"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={show ? 'Hide password' : 'Show password'}
            >
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-white mb-2">Confirm password</span>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); if (error) setError('') }}
              placeholder="Re-enter your password"
              className="w-full pl-10 pr-11 py-3 bg-white/95 border border-gray-200 rounded-lg input-focus text-gray-900 placeholder-gray-400"
            />
          </div>
        </label>

        {error && (
          <div className="bg-red-500/15 border border-red-300/40 text-red-100 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-white text-blue-900 font-semibold hover:bg-blue-50 transition disabled:opacity-60 disabled:cursor-not-allowed shadow-lg"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Creating your account…
            </>
          ) : (
            'Create account & sign in'
          )}
        </button>

        <p className="text-center text-sm text-blue-100/80">
          Already have access?{' '}
          <Link to="/login" className="text-white font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </form>
    </AuthShell>
  )
}

const ExistsPanel = ({ email, onBack }: { email: string; onBack: () => void }) => (
  <div className="space-y-5">
    <p className="text-blue-50 text-sm leading-relaxed">
      <span className="font-semibold">{email}</span> already has a Shorewood account.
      Sign in with your password, or reset it if you've forgotten.
    </p>
    <div className="flex flex-col gap-3">
      <Link
        to="/login"
        className="w-full text-center py-3 px-6 rounded-full bg-white text-blue-900 font-semibold hover:bg-blue-50 transition shadow-lg"
      >
        Sign in
      </Link>
      <Link
        to="/forgot-password"
        className="w-full text-center py-3 px-6 rounded-full border border-white/40 bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors"
      >
        Forgot your password?
      </Link>
      <button
        type="button"
        onClick={onBack}
        className="text-blue-200 hover:text-white text-sm underline-offset-2 hover:underline"
      >
        Use a different email
      </button>
    </div>
  </div>
)

const NotEligiblePanel = ({ email, onTryAnother }: { email: string; onTryAnother: () => void }) => (
  <div className="space-y-5">
    <div>
      <h3 className="text-xl font-semibold text-white mb-2">
        We don't see <span className="text-blue-200">{email}</span> on the member list.
      </h3>
      <p className="text-blue-100/90 text-sm leading-relaxed">
        Access to the Shorewood portal is limited to current association members.
        If you should be on the list — for example you've recently bought a cottage,
        or the board has you under a different email — please reach out.
      </p>
    </div>
    <a
      href="mailto:board@shorewoodmi.com?subject=Portal%20access%20request"
      className="block w-full text-center py-3 px-6 rounded-full bg-white text-blue-900 font-semibold hover:bg-blue-50 transition shadow-lg"
    >
      Email the board
    </a>
    <button
      type="button"
      onClick={onTryAnother}
      className="block w-full text-center py-3 px-6 rounded-full border border-white/40 hover:bg-white/10 transition text-white font-medium"
    >
      Try a different email
    </button>
  </div>
)

const AuthShell = ({
  children,
  title,
  subtitle,
}: {
  children: React.ReactNode
  title: string
  subtitle: string
}) => (
  <div
    className="min-h-screen w-full flex items-center justify-center p-4 relative overflow-hidden bg-deepwater"
    style={{
      backgroundImage: 'url(/fonts/images/login_background.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
    }}
  >
    <div
      className="absolute inset-0 bg-gradient-to-b from-deepwater/85 via-deepwater/55 to-deepwater/95"
      aria-hidden="true"
    />
    <div
      className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(201,160,82,0.10),transparent_55%)]"
      aria-hidden="true"
    />
    <div
      className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent"
      aria-hidden="true"
    />
    <div className="relative z-10 w-full max-w-md">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-blue-100 hover:text-white text-sm mb-6"
      >
        <ArrowLeft size={14} /> Back
      </Link>
      <div className="text-center mb-8">
        <h1 className="serif-font text-3xl font-bold text-white mb-1">{title}</h1>
        <p className="text-blue-100/80 text-sm">{subtitle}</p>
      </div>
      <div className="glass-effect bg-white/10 border-white/20 rounded-2xl p-7 shadow-2xl">
        {children}
      </div>
    </div>
  </div>
)

export default SignupPage
